// ─────────────────────────────────────────────────────────────
// Firebase configs for prod + dev (staging) environments.
// Selection driven by REACT_APP_FIREBASE_TARGET at build time:
//   - REACT_APP_FIREBASE_TARGET=dev  → DEV_CONFIG
//   - anything else / unset          → PROD_CONFIG  (safe default)
//
// CRA inlines REACT_APP_* vars at build time, so the bundle that ships
// to walterstodotool-dev.web.app contains DEV_CONFIG, and the bundle
// that ships to listicity.app contains PROD_CONFIG. The selection is
// frozen into the JS — no runtime hostname sniffing — which means
// `firebase use prod` + `REACT_APP_FIREBASE_TARGET=dev npm run build`
// + `firebase deploy --only hosting` is *not* a working combo (you'd
// upload a dev-config bundle to prod hosting). The npm scripts in
// package.json wire env+target together so this can't happen by
// accident; deploy through them, not by hand.
// ─────────────────────────────────────────────────────────────

// Walter 2026-05-09: prod authDomain is `listicity.app`, NOT the
// default `walterstodotool.firebaseapp.com`. This matters because
// iOS Safari (and other browsers with strict third-party-storage
// policies) partition sessionStorage by top-frame origin. When the
// app is loaded at listicity.app and the auth handler lives at
// firebaseapp.com, the post-redirect handler can't read the state
// the app wrote — sign-in surfaces "missing initial state" errors.
// Hosting the auth handler at the same origin as the app fixes it.
//
// Side-effects to know:
//   - `listicity.app` MUST be in Firebase Auth → Authorized domains
//     (Firebase Console → Authentication → Settings). It already is.
//   - Magic-link emails contain links pointing at
//     `listicity.app/__/auth/action`, not the old firebaseapp.com URL.
//   - Sign-in at legacy `walterstodotool.web.app` /
//     `walterstodotool.firebaseapp.com` URLs will now do a cross-
//     origin dance to listicity.app. Those domains are not the
//     canonical entry point; users should be on listicity.app.
const PROD_CONFIG = {
  apiKey: "AIzaSyDj8IkzPoEKS2oeXg6RxlvUzIMWVs0k0Ag",
  authDomain: "listicity.app",
  projectId: "walterstodotool",
  storageBucket: "walterstodotool.firebasestorage.app",
  messagingSenderId: "355868082190",
  appId: "1:355868082190:web:5a7d8e33524b04ed407c5f"
};

// Walter 2026-05-27: dev / staging environment created during the
// pre-F&F bifurcation. Hosted at walterstodotool-dev.web.app. Uses the
// default `walterstodotool-dev.firebaseapp.com` authDomain — no custom
// domain, no DNS work needed. Sign-in via magic-link works out of the
// box; Google OAuth is disabled on dev until a separate GIS Web Client
// ID is configured in Google Cloud Console (see GOOGLE_OAUTH_CLIENT_ID
// below).
const DEV_CONFIG = {
  apiKey: "AIzaSyB7X8VSoYl_scBYZ2aPcg6dLxcUBssIqn8",
  authDomain: "walterstodotool-dev.firebaseapp.com",
  projectId: "walterstodotool-dev",
  storageBucket: "walterstodotool-dev.firebasestorage.app",
  messagingSenderId: "228877419470",
  appId: "1:228877419470:web:7b45f03e6963fc2d25e19f"
};

const TARGET = process.env.REACT_APP_FIREBASE_TARGET || 'prod';
const firebaseConfig = TARGET === 'dev' ? DEV_CONFIG : PROD_CONFIG;

// Tag the global so console / DevTools spot-checks can confirm which
// env this bundle was built for. Cheap, doesn't leak anything new
// (projectId is already visible in every Firebase request).
if (typeof window !== 'undefined') {
  window.__LISTICITY_ENV__ = {
    target: TARGET,
    projectId: firebaseConfig.projectId,
  };
}

// Walter 2026-05-16: Google Identity Services (GIS) OAuth 2.0 Web
// Client ID. Used for the GIS-based sign-in flow that bypasses
// Firebase's popup/redirect dance — fixes "missing initial state"
// errors on Android Chrome where storage partitioning breaks the
// post-redirect handler.
//
// Where to find it:
//   Firebase Console → Authentication → Sign-in method → Google →
//   click the row to expand → "Web SDK configuration" → "Web client ID"
// OR
//   console.cloud.google.com/apis/credentials → look under "OAuth 2.0
//   Client IDs" for the auto-created "Web client (auto created by
//   Google Service)" entry → copy Client ID.
//
// Format: <project-number>-<random-suffix>.apps.googleusercontent.com
//
// SAFE TO COMMIT — this ID is public by design (it's sent in every
// OAuth request and visible in any browser network tab). Security
// comes from the authorized-origins list on Google's side, not from
// keeping the ID secret.
//
// Walter 2026-05-28: Google sign-in enabled on dev for full dev/prod
// parity (Walter's call: better to keep code paths identical than to
// special-case dev for an auth feature). Web Client ID auto-created
// by Firebase when Google sign-in was enabled in the dev project's
// Auth providers panel. Origin `https://walterstodotool-dev.web.app`
// added to the GCP OAuth client's authorized-JavaScript-origins list
// (the .firebaseapp.com alias was pre-populated by Firebase; we need
// the .web.app alias too since that's where our hosting lives).
const PROD_GOOGLE_OAUTH_CLIENT_ID = "355868082190-n6fm3tvtoqjbtmsm18e651dvdhhomkmk.apps.googleusercontent.com";
const DEV_GOOGLE_OAUTH_CLIENT_ID = "228877419470-dqtt8l6gt2guh0br8tcmvn65g46nbrgm.apps.googleusercontent.com";

export const GOOGLE_OAUTH_CLIENT_ID =
  TARGET === 'dev' ? DEV_GOOGLE_OAUTH_CLIENT_ID : PROD_GOOGLE_OAUTH_CLIENT_ID;

export const IS_DEV_TARGET = TARGET === 'dev';

// Cloudflare Turnstile (bot-check on the sign-up waitlist). The SITE key
// is public — it's embedded in the rendered widget; only the matching
// SECRET (Firebase secret TURNSTILE_SECRET, read by the joinWaitlist
// function) stays private. One widget covers both prod (listicity.app)
// and dev (walterstodotool-dev.web.app) — both hostnames are registered
// on it — so the key is the same in both environments.
export const TURNSTILE_SITE_KEY = '0x4AAAAAADfdMLYdbDdgm9je';

export default firebaseConfig;
